/* @flow */
import gql from 'graphql-tag';
import {pick} from 'lodash';
import {fragments as hitCollectionFragments} from '../components/HitCollection/HitCollection';
import {fragments as mapFragments} from '../components/Map/Map';
import {fragments as pagerFragments} from '../components/Pager/Pager';
import {fragments as destinationMessage} from '../components/SearchBannerContainer/destinationMessage';
import {fragments as footerSeoLinksFragments} from '../components/ConnectedFooterSeoLinks/ConnectedFooterSeoLinks';
import {fragments as filterCountsFragments} from '../../universal/queries/filteredCountQuery';
import {fragments as expandedGroupsFragments} from '../components/ExpandedGroups/ExpandedGroups';
import {fragments as htmlTitleFragments} from '../components/HtmlTitle/HtmlTitle';
import {fragments as analyticsDataLayerFragments} from '../components/AnalyticsDataLayer/AnalyticsDataLayer';
import {fragments as seoLinkpileFragments} from '../components/SeoLinkpile/SeoLinkpile';

import {ResultsParams} from '../../server/graphql/params';

import type {SearchParamsType} from '../reducers/searchParams';
import type {FilterGroupType} from '../components/MoreFilters/MoreFilters';
import type {ListingShape} from '../components/Hit/Hit';
import type {SERPHitShape} from '../components/HitCollection/HitCollection';
import type {MapViewport} from '../components/Map/Map';
import type {Shape as ExpandedGroupsShape} from '../components/ExpandedGroups/ExpandedGroups';
import type DestinationMessage from '@homeaway/graphql-traveler-urgency-messaging';
import type {AlternativeLink} from '../components/HtmlTitle/HtmlTitle';
import type {Props as SeoLinkpile} from '../components/SeoLinkpile/SeoLinkpile';

export type SearchReview = {
    detailPageUrl: string,
    reviewHead: string,
    reviewText: string,
    reviewCreated: string,
    reviewArrival: string,
    reviewRating: number,
    hitThumbnailUrl: string
};

export type SearchTerm = {
    description: string,
    name: string,
    termId: string
};

export type Coordinates = {
    latitude: number,
    longitude: number
};

export type SearchGeography = {
    name?: string,
    description?: string,
    location?: Coordinates,
    isGeocoded?: boolean,
    shouldShowMapCentralPin: boolean,
    lbsId?: string,
    primaryGeoType?: string,
    breadcrumbs: SearchGeography[],
    countryCode?: string
};

type Internal = {
    searchServiceUrl: string
};

export type HitCollection = {
    hits?: SERPHitShape[]
};

export type BreadcrumbShape = {
    url: string,
    name: string
};

export type DestinationBreadcrumbShape = {
    name: string,
    url: string
};

export type DestinationShape = {
    breadcrumbs?: BreadcrumbShape[],
    links?: BreadcrumbShape[]
};

export type CoreFilters = {
    adults?: number,
    children?: number,
    pets?: number,
    minBedrooms?: number,
    maxBedrooms?: number,
    minSleeps?: number,
    maxSleeps?: number,
    minPrice?: number,
    maxPrice?: number,
    minBathrooms?: number,
    maxBathrooms?: number
};

export type SearchDateRange = {
    arrivalDate: string,
    departureDate: string
};

export type SearchSort =
    | 'DEFAULT_SORT'
    | 'RATING_DESC'
    | 'BEDROOMS_DESC'
    | 'BEDROOMS_ASC'
    | 'PRICE_DESC'
    | 'PRICE_ASC'
    | 'NUM_REVIEWS_DESC';

export type ParsedSearchParams = {
    q?: string,
    coreFilters?: CoreFilters,
    dates?: SearchDateRange,
    sort: SearchSort
};

export type SearchQueryResults = {
    destination?: DestinationShape,
    destinationBreadcrumbs?: DestinationBreadcrumbShape[],
    urgencyDestinationMessage?: DestinationMessage,
    expandedGroups?: ExpandedGroupsShape,
    headlineTitle?: string,
    internal?: Internal,
    liveRegionRedirectUrl?: string,
    pidRedirectUrl?: string,
    pageSize?: number,
    page?: number,
    pageCount?: number,
    resultCount?: number,
    hitCollection?: HitCollection,
    listings?: ListingShape[],
    pageTitle?: string,
    mapViewport: MapViewport,
    metaDescription?: string,
    metaKeywords?: string,
    searchTerm?: ?SearchTerm,
    searchReviewsHeadline?: string,
    searchReviews?: SearchReview[],
    filterGroups?: FilterGroupType[],
    canonicalLink?: String,
    alternativeLinks?: AlternativeLink[],
    searchGeography?: SearchGeography,
    seoLinkpile?: SeoLinkpile,
    parsedParams?: ParsedSearchParams
};

export type SearchQueryResponseData = {
    results: ?SearchQueryResults,
    requestmarker: string,
    error?: Error,
    loading: boolean,
    variables: {params: SearchParamsType, url: string}
};

export const srsParams = (params: SearchParamsType) => {
    const newParams = pick(params, ResultsParams);
    const regionId = newParams.regionId;
    if (regionId && typeof regionId === 'string') {
        newParams.regionId = parseInt(regionId, 10);
    }
    const pinnedPropertyListing = newParams.pinnedPropertyListing;
    if (pinnedPropertyListing && typeof pinnedPropertyListing !== 'string') {
        newParams.pinnedPropertyListing = `${pinnedPropertyListing}`;
    }
    return newParams;
};

export const searchQueryVariables = (params: SearchParamsType, url: string) => {
    let include = ['percentBooked'];
    return {
        params: {
            ...srsParams(params),
            include
        },
        url
    };
};

export const FORCE_GRAPHQL_ERROR_HEADER = 'ha-serp-force-graphql-err';

const searchQuery = gql`
    query SearchQuery($params: ResultsParams, $url: String) {
        results(params: $params) {
            id
            destinationBreadcrumbs {
                name
                url
            }
            headlineTitle
            ...ExpandedGroupsResults
            ...Pager
            internal {
                searchServiceUrl
            }
            liveRegionRedirectUrl
            pidRedirectUrl
            searchReviewsHeadline
            searchReviews {
                detailPageUrl
                reviewHead
                reviewText
                reviewCreated
                reviewArrival
                reviewRating
                hitThumbnailUrl
            }
            searchTerm {
                description
                name
                termId
            }
            searchGeography {
                lbsId
                location {
                    latitude
                    longitude
                }
                isGeocoded
                shouldShowMapCentralPin
            }
            ...DestinationMessageResults
            ...FilterCountsResults
            ...FooterSeoLinksResults
            ...HitCollectionResults
            ...HtmlTitleResults
            ...ADLResults
            ...MapResults
            ...SeoLinkpileResults
        }
        ...RequestMarkerFragment
    }
    ${hitCollectionFragments.results}
    ${expandedGroupsFragments.results}
    ${filterCountsFragments.results}
    ${footerSeoLinksFragments.results}
    ${mapFragments.results}
    ${pagerFragments.hitCollection}
    ${htmlTitleFragments.results}
    ${destinationMessage.results}
    ${seoLinkpileFragments.results}
    ${analyticsDataLayerFragments.results}
    ${analyticsDataLayerFragments.query}
`;

export default searchQuery;
